// Self-hosted Protomaps basemap wiring.
//
// Replaces CARTO raster tiles, which since August 2026 come back
// watermarked "API KEY REQUIRED" without a key — and CARTO's terms forbid
// proxying or caching tiles server-side, so a key could not be hidden.

import { type Flavor, namedFlavor } from "@protomaps/basemaps";
import type { LatLngBoundsLiteral } from "leaflet";

/**
 * Protomaps "light" with the land/water contrast the old CARTO Voyager
 * basemap had. The flat grey of the default flavors made the Canaries
 * nearly invisible against the Atlantic on a phone screen, which is why
 * Voyager was picked over CARTO's light_all in the first place. Parks
 * and buildings are also nudged warmer so they sit with the creamier
 * earth tone instead of the default flavor's cool greys.
 */
export const BASEMAP_FLAVOR: Flavor = {
  ...namedFlavor("light"),
  earth: "#f7f2e8",
  water: "#a9d5e8",
  park_a: "#dbe8d2",
  park_b: "#b8dcae",
  buildings: "#e3ddd0",
};

/** Zoom the archive was extracted at; higher zooms are overzoomed. */
export const TILE_MAX_DATA_ZOOM = 14;

/**
 * Below this the archive no longer covers the visible world and the map
 * breaks into white patches — the renderer draws missing tiles as empty
 * rather than falling back to a lower zoom.
 */
export const TILE_MIN_ZOOM = 5;

/**
 * The map's maxBounds: the OUTER tier of the archive (z0-8,
 * scripts/basemap-region-outer.geojson), not the z9-14 core that hugs
 * Spain and the Canaries. The margin is what lets fitBounds centre a
 * region. With the old core-tight bounds (south edge 27.4°N, El Hierro at
 * 27.6°N) Leaflet shifted every Canaries view north until the viewport's
 * bottom touched the bound, pinning the archipelago to the bottom edge
 * on a portrait phone.
 */
export const BASEMAP_BOUNDS: LatLngBoundsLiteral = [
  [20, -25],
  [50, 12],
];

/**
 * The CORE tier of the archive (z9-14, scripts/basemap-region-core.geojson).
 * Past z8 only this rectangle has tiles; anywhere else in BASEMAP_BOUNDS a
 * view at z9+ is blank.
 */
export const BASEMAP_CORE_BOUNDS: LatLngBoundsLiteral = [
  [27.4, -18.4],
  [43.9, 4.5],
];

/** "Locate me" zoom — keeps a few neighbourhoods in frame for the list. */
export const LOCATE_ZOOM = 12;

/**
 * "Locate me" zoom for a user outside the core: the OUTER tier's maximum
 * zoom, so the view is fully drawn even where it extends past the core.
 */
export const LOCATE_OUTSIDE_ZOOM = 8;

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

/**
 * Where "locate me" should take the map. Inside the core: the user's own
 * position. Outside (Warsaw, Lyon, London): maxBounds would clamp a z12
 * view to the edge of the outer tier, where no z12 tile exists and the map
 * is blank — so move to the nearest point of the core rectangle at z8.
 */
export function locateTarget(
  lat: number,
  lng: number,
): { lat: number; lng: number; zoom: number } {
  const [[south, west], [north, east]] = BASEMAP_CORE_BOUNDS;
  const inside = lat >= south && lat <= north && lng >= west && lng <= east;
  if (inside) return { lat, lng, zoom: LOCATE_ZOOM };
  return {
    lat: clamp(lat, south, north),
    lng: clamp(lng, west, east),
    zoom: LOCATE_OUTSIDE_ZOOM,
  };
}

export const TILE_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://protomaps.com">Protomaps</a>';

/**
 * Tiles live under the variant prefix (`/paliwo/tiles/...`), not at the
 * server root, because each parent portal proxies exactly one prefix to
 * us. Same reasoning as DATA_BASE in data.ts. The archive version is a
 * path segment, so a basemap refresh gets fresh URLs instead of mixing
 * with a year of immutable-cached old tiles.
 */
export function tilesUrl(
  base: string = import.meta.env.BASE_URL,
  version: string = import.meta.env.VITE_BASEMAP_VERSION,
): string {
  return `${base}tiles/${version}/{z}/{x}/{y}.mvt`;
}

/** Label languages Protomaps carries that our UI can select. */
const PROTOMAPS_LANGS = ["pl", "en", "de", "fr", "it", "es"];

/**
 * Fullweb builds lock labels to the portal's language; the embed follows
 * its own language switcher. Spanish is the fallback — it is what the
 * source data carries natively.
 */
export function tileLang(site: string, uiLang: string): string {
  const candidate = site || uiLang;
  return PROTOMAPS_LANGS.includes(candidate) ? candidate : "es";
}
